import { useEffect, useState } from 'react';
import { Button, Input, Modal, Textarea } from '../../ui';
import {
  blankTaskTemplateDraft,
  validateTaskTemplateDraft,
} from '../../features/taskTemplates/taskTemplateModel';
import type { TaskTemplateDraft } from '../../features/taskTemplates/types';

export interface TaskTemplateEditorModalProps {
  open: boolean;
  /** Draft to edit; when omitted the modal opens a blank create form. */
  initial?: TaskTemplateDraft;
  /** Whether a save is in flight. */
  saving?: boolean;
  onClose: () => void;
  onSave: (draft: TaskTemplateDraft) => void;
}

/**
 * Create / edit modal for a task template. Collects a name, the Markdown content
 * (rendered inline by MarkdownView), and a "default template" flag. Reuses the
 * accessible {@link Modal}. Mirrors {@link ../Work/PrePlanEditorModal}.
 */
export function TaskTemplateEditorModal({
  open,
  initial,
  saving = false,
  onClose,
  onSave,
}: TaskTemplateEditorModalProps) {
  const isEdit = initial !== undefined;
  const [draft, setDraft] = useState<TaskTemplateDraft>(
    () => initial ?? blankTaskTemplateDraft(),
  );
  const [problems, setProblems] = useState<string[]>([]);

  // Reset the form whenever the modal (re)opens or its subject changes.
  useEffect(() => {
    if (open) {
      setDraft(initial ?? blankTaskTemplateDraft());
      setProblems([]);
    }
  }, [open, initial]);

  const patch = (fields: Partial<TaskTemplateDraft>) =>
    setDraft((prev) => ({ ...prev, ...fields }));

  const submit = () => {
    const found = validateTaskTemplateDraft(draft);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    onSave(draft);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? 'Edit template' : 'New task template'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {problems.length > 0 && (
          <ul
            className="space-y-1 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
            role="alert"
          >
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        <Input
          label="Name"
          value={draft.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="e.g. Feature task template"
          autoFocus
        />

        <Textarea
          label="Content (Markdown)"
          value={draft.content}
          onChange={(e) => patch({ content: e.target.value })}
          rows={16}
          placeholder={'# Goal\n\n# Deliverables\n\n# Tasks\n\n- [ ] Task 1\n    - [ ] Subtask 1.1 (effort: 1d)'}
          hint="Rendered inline with headings, checklists, tables, lists and code."
        />

        <label className="flex items-center gap-2 text-sm text-zinc-300">
          <input
            type="checkbox"
            checked={draft.isDefault ?? false}
            onChange={(e) => patch({ isDefault: e.target.checked })}
            className="h-4 w-4 rounded border-surface-border bg-surface-1 text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          />
          Make this the default template
        </label>
      </div>
    </Modal>
  );
}

export default TaskTemplateEditorModal;
